import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, Box, Button, Card, Checkbox, Chip, CircularProgress, FormControlLabel, Grid, IconButton, Radio, RadioGroup, TextField, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { AmexLogo, cardSurface, DinersLogo, DiscoverLogo, FourDots, GenericCardLogo, Guard, MasterCardLogo, VisaCardLogo } from '../../../shared/lib/assets';
import { Alert } from '../../../shared/lib/common/Alert';
import { CustomController } from '../../../shared/lib/common/CustomController';
import { PageHeading } from '../../../shared/lib/common/PageHeading';
import palette from '../../../shared/lib/theme/palette';
import { LinkStyle, UserDetailHeading, billingStepBackButton, billingStepBackButtonMobile, billingStepHeader, billingStepHeaderMobile, billingTermsRow, billingTermsRowFreePlan, flexCenter } from '../../../shared/lib/theme/styleConstants';
import { centToDollar } from '../../../shared/lib/utils';
import {
  LABEL_CITY,
  LABEL_COUNTRY,
  LABEL_FIRST_NAME,
  LABEL_LAST_NAME,
  LABEL_NOT_AVAILABLE,
  LABEL_PROMO_CODE,
  LABEL_STATE,
  LABEL_STREET_ADDRESS,
  LABEL_ZIP_CODE,
  PAGE_TITLE_BILLING_ADDRESS_SECTION,
  LABEL_STREET_ADDRESS_LINE_1,
  LABEL_STREET_ADDRESS_LINE_2,
  LABEL_OPTIONAL,
  LABEL_PAYMENT_METHOD,
  LABEL_CARD,
  LABEL_CREDIT,
  LABEL_SUBSCRIPTION,
  LABEL_TOTAL_BILLED,
  LABEL_TERMS_AND_CONDITIONS,
  MSG_I_AGREE_TO_THE,
  MSG_INCLUDING_TAXES_AND_FEES,
  BUTTON_TEXT_APPLY,
  BUTTON_TEXT_COMPLETE_CHECKOUT,
  NOTE_CANCEL_SUBSCRIPTION_30_DAYS,
  MSG_SECURE_CREDIT_CARD_PAYMENT,
  MSG_SSL_ENCRYPTED_PAYMENT,
  LABEL_PLAN_BASIC,
  LABEL_PLAN_STANDARD,
  HEADING_FINISH_ACCOUNT_SETUP,
  HEADING_UPDATE_BILLING_DETAILS,
  SUBHEADING_FILL_BILLING_DETAILS,
  LABEL_SUMMARY,
  LABEL_PLAN_YEAR,
  LABEL_PLAN_MONTH,
  COUNTRY_OPTION_US,
  CardBrands,
} from '../../../shared/lib/constants';
import { CARD_INFO_TEXT, COUNTRIES, EXPIRED_COUPON, LINKS, MSG_COUPON_APPLIED_SUCCESSFULLY, MSG_COUPON_CANNOT_BE_APPLIED, NO_RECORD_FOUND, PENDING_PROMO_CODE, PaymentMethods, PROMO_CODE_DOES_NOT_EXIST, SelectedPlanType, THE_PROMO_CODE_HAS_EXPIRED } from '../app/constants';
import { useAppContext } from '../app/contexts/AppContext';
import { BillingStepTwoProps, NewCreateSubscriptionData } from '../app/interfaceTypes';
import { ActionType } from '../app/reducer/AppContextReducer';
import { getInterval } from '../app/utils';
import { billingValidationSchema } from '../app/validationSchema';
import { Card as CardType, useCreateClientSecretMutation, useCreateSubscriptionMutation, useFetchBillingDetailsLazyQuery, useRetrieveCouponLazyQuery } from '../generated';
import InfoIcon from '@mui/icons-material/Info';
import { loadStripe } from '@stripe/stripe-js';
import CardPaymentModal from './CardPaymentModal';
import { Elements } from '@stripe/react-stripe-js';
import WalletPaymentModal from './WalletPaymentModal';
import StateSelector from '../../../shared/lib/common/StateSelector';
import { ArrowBack } from '@mui/icons-material';
import { SECONDARY_FONT, Ubuntu } from '../../../shared/lib/constants/fonts';
import { useMobileView } from '../app/hooks/useMobileView';

/**
 * BillingStepTwo component for handling billing information and payment.
 * @prop {Object} state - Current state.
 * @prop {Function} dispatch - Dispatch function for updating state.
 * @prop {Function} handleNext - Function to handle next step.
 */

const stripePublicKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
const stripe = loadStripe(stripePublicKey);

export const BillingStepTwo: FC<BillingStepTwoProps> = ({ state, handleNext, setBillingCard, isChangePlan }) => {
  countries.registerLocale(enLocale);

  const { blackOne, blackFive, lightTwo, red, blackTwo, white } = palette;
  const { selectedPlanValue, isFree, selectedPlan, plans } = state;
  const { id: selectedPlanId, nickname, amount: selectedPlanAmount } = selectedPlan ?? {};
  const currentPlans = plans.filter((plan) => plan.product === selectedPlan?.product);
  const productWithSmallestAmount = currentPlans.sort((a, b) => (a?.amount ?? Infinity) - (b?.amount ?? Infinity))[0];
  const { amount: smallestAmount, nickname: smallestAmountNickname, id: smallestAmountId } = productWithSmallestAmount;
  const productWithLargestAmount = currentPlans.sort((a, b) => (b?.amount || 0) - (a?.amount || 0))[0];
  const { amount: largestAmount, nickname: largestAmountNickname, id: largestAmountId } = productWithLargestAmount;
  const [productWithSmallestAmountDiscount, setProductWithSmallestAmountDiscount] = useState<number>(0);
  const [productWithLargestAmountDiscount, setProductWithLargestAmountDiscount] = useState<number>(0);
  const [promoCode, setPromoCode] = useState<string>('');
  const [disablePromoCode, setDisablePromoCode] = useState<boolean>(false);
  const { setCurrentUser, currentUser, dispatch: appDispatch } = useAppContext();
  const [checked, setChecked] = useState<boolean>(false);
  const smallViewport = useMediaQuery('(max-width:394px)');
  const fromMobile = useMobileView();
  const [discount, setDiscount] = useState<number>(0);
  const [price, setPrice] = useState(selectedPlanAmount);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethods>(PaymentMethods.CARD);
  const [clientSecret, setClientSecret] = useState<string>('');
  const [walletModal, setWalletModal] = useState<boolean>(false);
  const [cardSubmit, setCardSubmit] = useState<boolean>(false);
  const [address, setAddress] = useState<CardType | undefined>(undefined);
  const [editAddress, setEditAddress] = useState<boolean>(false);
  const [editCard, setEditCard] = useState<boolean>(false);

  const { address_city, address_country, address_line1, address_line2, address_state, address_zip, customer_name, name, brand, exp_month, exp_year, last4 } = address ?? {};

  const methods = useForm<NewCreateSubscriptionData>({
    mode: 'all',
    // @ts-expect-error  //TODO: THIS ERROR IS BEING SUPPRESSED WE NEED TO FIX THIS LATER 8)
    resolver: yupResolver(billingValidationSchema(!isFree && paymentMethod === PaymentMethods.CARD)),

    defaultValues: {
      cardDetail: {
        firstName: '',
        lastName: '',
        address_city: '',
        country: COUNTRY_OPTION_US.label,
        address_line1: '',
        address_line2: '',
        address_state: '',
        address_zip: '',
        expiry: '',
        cvc: '',
        number: ''
      },
      name: ''
    }
  });

  const { handleSubmit, control, watch, setValue } = methods;
  const { cardDetail } = watch();
  const { firstName, lastName } = cardDetail || {};

  const { isSubscribed } = currentUser || {};
  const { customerStripeId } = isSubscribed || {};

  const goToNextStep = () => {
    localStorage.removeItem(PENDING_PROMO_CODE);
    handleNext?.();
  };

  const [retrieveCoupon, { loading: retrieveCouponLoading }] = useRetrieveCouponLazyQuery({
    onError({ message }) {
      if (message === NO_RECORD_FOUND) Alert.error(PROMO_CODE_DOES_NOT_EXIST);
      else if (message === EXPIRED_COUPON) Alert.error(THE_PROMO_CODE_HAS_EXPIRED);
      else Alert.error(message);
    },

    onCompleted(data) {
      const {
        retrieveCoupon: { amountOff, percentOff, products }
      } = data;

      /**
       * if coupon contain empty product or null then coupon is applicable to all plans
       * if coupon contains products then it only apply to that plans that belong to provided products
       */
      if (!products || products?.length === 0 || (!disablePromoCode && selectedPlan?.product && products?.includes(selectedPlan?.product))) {
        if (amountOff) {
          setDiscount(amountOff);
          setProductWithLargestAmountDiscount(amountOff);
          setProductWithSmallestAmountDiscount(amountOff);
        } else if (percentOff) {
          largestAmount && setProductWithLargestAmountDiscount((percentOff / 100) * largestAmount);
          smallestAmount && setProductWithSmallestAmountDiscount((percentOff / 100) * smallestAmount);
          selectedPlanAmount && setDiscount((percentOff / 100) * selectedPlanAmount);
        }

        setPrice(selectedPlanAmount ? selectedPlanAmount - discount : 0);
        setDisablePromoCode(true);
        Alert.success(MSG_COUPON_APPLIED_SUCCESSFULLY);
      } else {
        Alert.error(MSG_COUPON_CANNOT_BE_APPLIED);
      }
    }
  });

  const [getBillingDetail] = useFetchBillingDetailsLazyQuery({
    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const { fetchCardDetails } = data;
      setAddress(fetchCardDetails ?? undefined);
    }
  });

  const [createSubscription, { loading: subscriptionLoading }] = useCreateSubscriptionMutation({
    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const {
        createSubscription: { customerStripeId }
      } = data;
      const id = customerStripeId;

      currentUser && setCurrentUser({ ...currentUser, isSubscribed: { customerStripeId: id } });
      goToNextStep();
    }
  });

  const [createClientSecret, { loading: clientLoading }] = useCreateClientSecretMutation({
    onCompleted: (data) => {
      const { createClientSecret } = data;
      const { clientSecret } = createClientSecret || {};
      if (clientSecret) {
        setClientSecret(clientSecret || '');
        setWalletModal(true);
      } else {
        goToNextStep();
      }
    },

    onError({ message }) {
      setClientSecret('');
      Alert.error(message);
    }
  });

  const fetchBillingDetail = async (customerStripeId: string) => {
    await getBillingDetail({
      variables: {
        fetchCardDetailsInput: customerStripeId
      }
    });
  };

  const onApply = async () => {
    promoCode &&
      (await retrieveCoupon({
        variables: {
          retrieveCouponInput: promoCode
        }
      }));
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPromoCode(event.target.value);
  };

  const onError = () => {
    setEditAddress(true);
  };

  const createStripeClientSecret = async () => {
    if (paymentMethod === PaymentMethods.GOOGLE_PAY && selectedPlanId) {
      await createClientSecret({
        variables: {
          createSubscriptionInput: {
            price: updatedProductCount > 1 ? (selectedPlanValue === SelectedPlanType.YEAR ? largestAmountId : smallestAmountId) : selectedPlanId,
            name: firstName || lastName ? `${firstName} ${lastName}` : LABEL_NOT_AVAILABLE,
            description: updatedProductCount > 1 ? (selectedPlanValue === SelectedPlanType.YEAR ? largestAmountNickname : smallestAmountNickname) : nickname,
            coupon: disablePromoCode ? promoCode : ''
          }
        }
      });
    }
  };

  const createStripeSubscription = async (token?: string) => {
    if ((isFree || paymentMethod === PaymentMethods.CARD) && selectedPlanId) {
      await createSubscription({
        variables: {
          createSubscriptionInput: {
            ...(token && { token: token ?? '' }),
            price: updatedProductCount > 1 ? (selectedPlanValue === SelectedPlanType.YEAR ? largestAmountId : smallestAmountId) : selectedPlanId,
            name: firstName || lastName ? `${firstName} ${lastName}` : LABEL_NOT_AVAILABLE,
            description: updatedProductCount > 1 ? (selectedPlanValue === SelectedPlanType.YEAR ? largestAmountNickname : smallestAmountNickname) : nickname,
            coupon: disablePromoCode ? promoCode : ''
          }
        }
      });
    }
  };

  const onSubmit = async () => {
    if (checked) {
      appDispatch({ type: ActionType.SET_SELECTED_PLAN, selectedPlan: selectedPlan! });

      if (isFree) {
        await createStripeSubscription();
      } else if (!isFree && paymentMethod === PaymentMethods.CARD && last4 && !editCard) {
        await createStripeSubscription();
      } else if (!isFree && paymentMethod === PaymentMethods.CARD) {
        setCardSubmit(true);
      } else if (paymentMethod === PaymentMethods.GOOGLE_PAY) {
        await createStripeClientSecret();
      }
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => setChecked(event.target.checked);

  const { metadata, interval } = selectedPlan ?? {};
  const { productCount } = metadata ?? {};
  const updatedProductCount = parseInt(productCount);

  const goBackToFirstStep = () => {
    setBillingCard(false);
  };

  function isIOS(): boolean {
    return /iPhone|iPad|Macintosh|Mac|iPod/i.test(navigator.userAgent);
  }

  const brandLogoMap: Record<string, () => JSX.Element> = {
    [CardBrands.VISA]: VisaCardLogo,
    [CardBrands.MASTERCARD]: MasterCardLogo,
    [CardBrands.American_Express]: AmexLogo,
    [CardBrands.DISCOVER]: DiscoverLogo,
    [CardBrands.DINERSCLUB]: DinersLogo,
    [CardBrands.GENERIC]: GenericCardLogo
  };

  const renderCardLogo = (brand: string): JSX.Element => {
    const logoFn = brandLogoMap[brand.toLowerCase()] || GenericCardLogo;
    return logoFn();
  };

  useEffect(() => {
    if (isFree) return;
    const raw = localStorage.getItem(PENDING_PROMO_CODE);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      const { code, savedAt } = parsed;
      const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
      if (
        typeof code === 'string' &&
        code.trim().length > 0 &&
        code.trim().length <= 50 &&
        typeof savedAt === 'number' &&
        Date.now() - savedAt < SEVEN_DAYS_MS
      ) {
        setPromoCode(code.trim());
      }
    } catch {
      // ignore malformed data
    }
  }, [isFree]);

  useEffect(() => {
    console.log('Address', customerStripeId);

    if (customerStripeId) {
      (async () => await fetchBillingDetail(customerStripeId))();
    }
  }, [customerStripeId]);

  useEffect(() => {
    if (address) {
      setValue('cardDetail.address_line1', address_line1 ?? '');
      setValue('cardDetail.address_line2', address_line2 ?? '');
      setValue('cardDetail.address_city', address_city ?? '');
      setValue('cardDetail.address_country', address_country ?? '');
      setValue('cardDetail.address_state', address_state ?? '');
      setValue('cardDetail.address_zip', address_zip ?? '');
      setValue('cardDetail.firstName', customer_name || name || '');
    }
  }, [address]);

  const loading = subscriptionLoading || clientLoading;

  return (
    <Box display="flex" flexDirection="column" alignItems="center" width="100%" sx={{ px: fromMobile ? 0 : 2, py: fromMobile ? 0.5 : 2 }}>

      {/* Header — title centered like BillingStepOne; back button floats left on
          desktop and sits above the title on mobile. */}
      <Box sx={fromMobile ? billingStepHeaderMobile : billingStepHeader}>
        <IconButton onClick={goBackToFirstStep} color="primary" sx={fromMobile ? billingStepBackButtonMobile : billingStepBackButton}>
          <ArrowBack fontSize="small" />
        </IconButton>

        <Box sx={{ '& > div': { justifyContent: 'center' } }}>
          <PageHeading
            variant={fromMobile ? 'h5_mobile' : 'h3'}
            color={blackOne}
            title={isChangePlan ? HEADING_UPDATE_BILLING_DETAILS : HEADING_FINISH_ACCOUNT_SETUP}
            subTitle={SUBHEADING_FILL_BILLING_DETAILS}
          />
        </Box>
      </Box>

      <Box width="100%" maxWidth="100%" sx={{ px: fromMobile ? 0 : 3, py: fromMobile ? 0.5 : 2 }}>
        <FormProvider {...methods}>
          <form autoComplete="off" onSubmit={handleSubmit(onSubmit, onError)}>
            <Grid container spacing={fromMobile ? 0 : 2} rowSpacing={2}>
              {!isFree && (
                <Grid item md={fromMobile ? 12 : 6} xs={12}>
                  <Box mb="10px" sx={{ p: fromMobile ? '15px' : '40px' }} border={`1px dashed ${blackFive}`} borderRadius="16px">
                    <Box display="flex">
                      <Typography mr={1} variant="h6" sx={{ fontWeight: '700', color: blackOne, paddingBottom: '34px' }}>
                        {LABEL_PAYMENT_METHOD}
                      </Typography>
                      <Tooltip title={CARD_INFO_TEXT}>
                        <InfoIcon />
                      </Tooltip>
                    </Box>

                    <Grid container spacing={fromMobile ? 0 : 2} rowSpacing={2}>
                      <Grid item xs={12}>
                        <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as PaymentMethods)}>
                          {Object.entries(PaymentMethods).map(([key, label]) => {
                            return (
                              <Card
                                key={key}
                                variant="outlined"
                                onClick={() => setPaymentMethod(label as PaymentMethods)}
                                sx={{
                                  p: 1,
                                  mb: 2,
                                  borderColor: paymentMethod === label ? 'primary.main' : 'grey.300',
                                  borderRadius: 2,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <FormControlLabel
                                  value={label}
                                  control={<Radio />}
                                  label={<Typography>{label === PaymentMethods.GOOGLE_PAY ? (isIOS() ? PaymentMethods.GOOGLE_PAY : 'Google Pay') : label}</Typography>}
                                  sx={{ flexGrow: 1 }}
                                />
                              </Card>
                            );
                          })}
                        </RadioGroup>
                      </Grid>
                    </Grid>
                  </Box>

                  {paymentMethod === PaymentMethods.CARD && (
                    <>
                      <Box mb="10px" sx={{ p: fromMobile ? '15px' : '40px' }} border={`1px dashed ${blackFive}`} borderRadius="16px">
                        <Box display={'flex'} justifyContent={'space-between'}>
                          <Typography variant="h6" sx={{ fontWeight: '700', color: blackOne, paddingBottom: '34px' }}>
                            {LABEL_CARD}
                          </Typography>

                          <IconButton color="primary" size="large" disabled={!address} onClick={() => setEditCard((prev) => !prev)}>
                            {editCard && address ? <ArrowBackIcon /> : <EditIcon />}
                          </IconButton>
                        </Box>
                        {!editCard && last4 ? (
                          <Box position="relative" height={{ xs: 150, sm: 170, md: 185 }} maxWidth={{ xs: '100%', sm: 335 }} width="100%" mx="auto">
                            {/* Card background: only show on large screens and up */}
                            <Box width="100%" height="100%" sx={{ display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' } }}>
                              {cardSurface()}
                            </Box>

                            {/* Overlay content */}
                            <Box
                              position="absolute"
                              height="100%"
                              width="100%"
                              top={0}
                              sx={{
                                opacity: 1,
                                p: { xs: 1, sm: '12px 8px', md: '16px 10px' },
                                borderRadius: 2,
                                background: {
                                  xs: 'linear-gradient(135deg, #3a3a3a, #5a5a5a)', // fallback bg for small screens
                                  lg: 'transparent'
                                }
                              }}
                            >
                              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 700,
                                      color: white,
                                      fontFamily: Ubuntu,
                                      fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' }
                                    }}
                                  >
                                    {LABEL_CREDIT}
                                  </Typography>
                                  {brand && renderCardLogo(brand)}
                                </Box>

                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 700,
                                      color: white,
                                      pb: '5px',
                                      fontFamily: SECONDARY_FONT,
                                      fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' }
                                    }}
                                  >
                                    {name}
                                  </Typography>

                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        color: white,
                                        fontFamily: SECONDARY_FONT,
                                        fontSize: { xs: '0.72rem', md: '0.9rem' }
                                      }}
                                    >
                                      {FourDots()} {last4}
                                    </Typography>

                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 700,
                                        color: white,
                                        fontFamily: SECONDARY_FONT,
                                        fontSize: { xs: '0.72rem', md: '0.9rem' }
                                      }}
                                    >
                                      EXP : {`${exp_month} / ${exp_year}`}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          <Elements stripe={stripe}>
                            <CardPaymentModal
                              createSubscription={createStripeSubscription}
                              cardSubmit={cardSubmit}
                              onError={() => {
                                setCardSubmit(false);
                              }}
                            />
                          </Elements>
                        )}
                      </Box>

                      <Box mb="40px" sx={{ p: fromMobile ? '15px' : '40px' }} border={`1px dashed ${blackFive}`} borderRadius="16px">
                        <Box display={'flex'} justifyContent={'space-between'}>
                          <Typography variant="h6" sx={{ fontWeight: '700', color: blackOne, paddingBottom: '34px' }}>
                            {PAGE_TITLE_BILLING_ADDRESS_SECTION}
                          </Typography>

                          <IconButton color="primary" size="large" disabled={!address} onClick={() => setEditAddress((prev) => !prev)}>
                            {editAddress && address ? <ArrowBackIcon /> : <EditIcon />}
                          </IconButton>
                        </Box>
                        {!editAddress && address_line1 ? (
                          <Box>
                            <Grid container spacing={2} rowSpacing={4}>
                              <Grid item xs={12}>
                                <Typography variant="h2" sx={UserDetailHeading}>
                                  {LABEL_STREET_ADDRESS}
                                </Typography>
                                <Typography variant="body2" sx={{ color: blackOne, fontWeight: '600' }}>
                                  {address_line1 || address_line2 || LABEL_NOT_AVAILABLE}
                                </Typography>
                              </Grid>

                              <Grid item xs={fromMobile ? 6 : 12} md={6}>
                                <Typography variant="h2" sx={UserDetailHeading}>
                                  {LABEL_COUNTRY}
                                </Typography>
                                <Typography variant="body2" sx={{ color: blackOne, fontWeight: '600' }}>
                                  {(address_country && (countries.getName(address_country, 'en') || address_country)) || LABEL_NOT_AVAILABLE}
                                </Typography>
                              </Grid>

                              <Grid item xs={fromMobile ? 6 : 12} md={6}>
                                <Typography variant="h2" sx={UserDetailHeading}>
                                  {LABEL_STATE}
                                </Typography>
                                <Typography variant="body2" sx={{ color: blackOne, fontWeight: '600' }}>
                                  {address_state || LABEL_NOT_AVAILABLE}
                                </Typography>
                              </Grid>

                              <Grid item xs={fromMobile ? 6 : 12} md={6}>
                                <Typography variant="h2" sx={UserDetailHeading}>
                                  {LABEL_CITY}
                                </Typography>
                                <Typography variant="body2" sx={{ color: blackOne, fontWeight: '600' }}>
                                  {address_city || LABEL_NOT_AVAILABLE}
                                </Typography>
                              </Grid>

                              <Grid item xs={fromMobile ? 6 : 12} md={6}>
                                <Typography variant="h2" sx={UserDetailHeading}>
                                  {LABEL_ZIP_CODE}
                                </Typography>
                                <Typography variant="body2" sx={{ color: blackOne, fontWeight: '600' }}>
                                  {address_zip ?? '--'}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        ) : (
                          <>
                            <Grid container columnSpacing={fromMobile ? 1 : 2} rowSpacing={2}>
                              <Grid item md={6} xs={12}>
                                <CustomController controllerLabel={LABEL_FIRST_NAME} controllerName="cardDetail.firstName" />
                              </Grid>

                              <Grid item md={6} xs={12}>
                                <CustomController controllerLabel={LABEL_LAST_NAME} controllerName="cardDetail.lastName" />
                              </Grid>

                              <Grid item xs={12}>
                                <CustomController controllerLabel={LABEL_STREET_ADDRESS_LINE_1} controllerName="cardDetail.address_line1" />
                              </Grid>

                              <Grid item xs={12}>
                                <CustomController controllerLabel={LABEL_STREET_ADDRESS_LINE_2} controllerName="cardDetail.address_line2" tooltipText={LABEL_OPTIONAL} />
                              </Grid>

                              <Grid item md={6} xs={fromMobile ? 6 : 12}>
                                <Controller
                                  name="cardDetail.country"
                                  control={control}
                                  rules={{
                                    required: 'Country is required'
                                  }}
                                  render={({ field, fieldState }) => (
                                    <Autocomplete
                                      {...field}
                                      sx={{ marginTop: '8px' }}
                                      options={COUNTRIES}
                                      renderInput={(params) => <TextField {...params} label={LABEL_COUNTRY} error={!!fieldState.error} helperText={fieldState.error?.message} />}
                                      onChange={(_, data) => field.onChange(data)}
                                    />
                                  )}
                                />
                              </Grid>

                              <Grid item md={6} xs={fromMobile ? 6 : 12}>
                                <StateSelector label={LABEL_STATE} name="cardDetail.address_state" />
                              </Grid>

                              <Grid item md={6} xs={fromMobile ? 6 : 12}>
                                <CustomController controllerLabel={LABEL_CITY} controllerName="cardDetail.address_city" />
                              </Grid>

                              <Grid item md={6} xs={fromMobile ? 6 : 12}>
                                <CustomController controllerLabel={LABEL_ZIP_CODE} controllerName="cardDetail.address_zip" />
                              </Grid>
                            </Grid>
                          </>
                        )}
                      </Box>
                    </>
                  )}
                </Grid>
              )}

              <Grid item md={isFree || fromMobile ? 12 : 6} xs={12}>
                <Box sx={{ position: fromMobile ? 'static' : 'sticky', top: 0 }}>
                  <Box sx={{ backgroundColor: lightTwo, p: fromMobile ? '15px' : '40px' }} borderRadius="16px">
                    <Typography variant="h6" sx={{ fontWeight: '700', color: blackOne, paddingBottom: '34px' }}>
                      {LABEL_SUMMARY}
                    </Typography>

                    <Box display="flex" justifyContent="space-between" mb="20px">
                      <Typography variant="subtitle2" sx={{ color: blackTwo, fontWeight: '600' }}>
                        {LABEL_SUBSCRIPTION}
                      </Typography>

                      <Chip size="small" label={isFree ? LABEL_PLAN_BASIC : LABEL_PLAN_STANDARD} sx={{ fontSize: '12px', color: 'white', backgroundColor: red }} />
                    </Box>

                    <Box display="flex" paddingRight="20px" justifyContent="flex-end" width="100%" borderBottom={`1px dashed ${blackFive}`}>
                      <Box position="relative">
                        <Typography variant="subtitle2" sx={{ position: 'absolute', top: '13px', left: '-10px' }}>
                          $
                        </Typography>

                        {updatedProductCount === 1 && (
                          <Typography variant="subtitle1" sx={{ color: blackOne, marginBottom: '24px', fontWeight: '700' }}>
                            {price && centToDollar(price - discount) > 0 ? parseFloat(centToDollar(price - discount).toString()).toFixed(2) : '0.00'}
                          </Typography>
                        )}

                        {updatedProductCount > 1 && largestAmount && smallestAmount && (
                          <Typography variant="subtitle1" sx={{ color: blackOne, marginBottom: '24px', fontWeight: '700' }}>
                            {selectedPlanValue === SelectedPlanType.YEAR
                              ? centToDollar(largestAmount) - centToDollar(productWithLargestAmountDiscount) > 0
                                ? parseFloat((centToDollar(largestAmount) - centToDollar(productWithLargestAmountDiscount)).toString()).toFixed(2)
                                : '0.00'
                              : centToDollar(smallestAmount) - centToDollar(productWithSmallestAmountDiscount) > 0
                                ? parseFloat((centToDollar(smallestAmount) - centToDollar(productWithSmallestAmountDiscount)).toString()).toFixed(2)
                                : '0.00'}
                          </Typography>
                        )}

                        {!isFree && updatedProductCount !== 1 && (
                          <Typography variant="subtitle2" sx={{ position: 'absolute', bottom: '30px', right: '-25px' }}>
                            {selectedPlanValue === SelectedPlanType.YEAR ? `/${LABEL_PLAN_YEAR}` : `/${LABEL_PLAN_MONTH}`}
                          </Typography>
                        )}

                        {updatedProductCount === 1 && (
                          <Typography variant="subtitle2" sx={{ position: 'absolute', bottom: '20px', right: '-25px' }}>
                            / {getInterval(interval)}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box display="flex" py="20px" justifyContent="space-between" mb="20px" borderBottom={`1px dashed ${blackFive}`}>
                      <Typography variant="h6" sx={{ color: blackOne, fontWeight: '600' }}>
                        {LABEL_TOTAL_BILLED}
                      </Typography>

                      {updatedProductCount === 1 && (
                        <Typography variant="h6" sx={{ color: blackOne, fontWeight: '600' }}>
                          ${price && centToDollar(price - discount) > 0 ? parseFloat(centToDollar(price - discount).toString()).toFixed(2) : '0.00'}
                        </Typography>
                      )}

                      {updatedProductCount > 1 && largestAmount && smallestAmount && (
                        <Typography variant="h6" sx={{ color: blackOne, fontWeight: '600' }}>
                          ${selectedPlanValue === SelectedPlanType.YEAR
                            ? centToDollar(largestAmount) - centToDollar(productWithLargestAmountDiscount) > 0
                              ? parseFloat((centToDollar(largestAmount) - centToDollar(productWithLargestAmountDiscount)).toString()).toFixed(2)
                              : '0.00'
                            : centToDollar(smallestAmount) - centToDollar(productWithSmallestAmountDiscount) > 0
                              ? parseFloat((centToDollar(smallestAmount) - centToDollar(productWithSmallestAmountDiscount)).toString()).toFixed(2)
                              : '0.00'}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={isFree ? billingTermsRowFreePlan : billingTermsRow}>
                      <Checkbox
                        sx={{ paddingLeft: '0', paddingRight: smallViewport ? '5px' : '9px', paddingTop: '4px' }}
                        checked={checked}
                        onChange={handleChange}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />

                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: blackTwo,
                            display: 'inline'
                          }}
                        >
                          {MSG_I_AGREE_TO_THE}
                          <Link to={LINKS.TERMS_AND_CONDITIONS_LINK} target="_blank" rel="noopener noreferrer" style={LinkStyle}>
                            {LABEL_TERMS_AND_CONDITIONS}
                          </Link>{' '}
                          {MSG_INCLUDING_TAXES_AND_FEES}
                        </Typography>
                      </Box>
                    </Box>

                    {!isFree && (
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb="24px">
                        <TextField label={LABEL_PROMO_CODE} margin="dense" fullWidth value={promoCode} onChange={onChange} />

                        <Button
                          onClick={onApply}
                          variant="outlined"
                          color="primary"
                          sx={{ height: '56px', marginLeft: '16px' }}
                          disabled={retrieveCouponLoading || disablePromoCode}
                          endIcon={retrieveCouponLoading && <CircularProgress size={20} />}
                        >
                          {BUTTON_TEXT_APPLY}
                        </Button>
                      </Box>
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      sx={{ marginBottom: '16px' }}
                      variant="contained"
                      color="primary"
                      disabled={loading || !checked}
                      endIcon={loading && <CircularProgress size={20} />}
                    >
                      {BUTTON_TEXT_COMPLETE_CHECKOUT}
                    </Button>

                    <Typography textAlign="center" color="primary" variant="h2" sx={{ fontSize: '11px', marginBottom: '24px' }}>
                      {NOTE_CANCEL_SUBSCRIPTION_30_DAYS}
                    </Typography>

                    <Box sx={flexCenter} marginBottom="8px">
                      {Guard()}

                      <Typography textAlign="center" variant="subtitle2" sx={{ marginTop: '1px', fontWeight: '600', color: blackOne }}>
                        {MSG_SECURE_CREDIT_CARD_PAYMENT}
                      </Typography>
                    </Box>

                    <Typography textAlign="center" variant="h2" sx={{ color: blackOne }}>
                      {MSG_SSL_ENCRYPTED_PAYMENT}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </form>

          {paymentMethod === PaymentMethods.GOOGLE_PAY && clientSecret && (
            <Elements stripe={stripe} options={{ clientSecret }}>
              <WalletPaymentModal
                open={walletModal}
                onClose={() => {
                  setWalletModal(false);
                }}
                clientSecret={clientSecret}
                gotoNext={goToNextStep}
              />
            </Elements>
          )}
        </FormProvider>
      </Box>
    </Box>
  );
};